import { useState } from "react";
import faqImg from "../assets/images/phone/5.png";
import ModalVideo from "react-modal-video";
import "../../node_modules/react-modal-video/scss/modal-video.scss";
import CountUp from "react-countup";
import { FiChevronUp, FiDownload, FiPlay } from "react-icons/fi";
import { Link as Link1 } from "react-scroll";
import { useTranslation } from "react-i18next";

interface FaqData {
  id: number;
  title: string;
  desc: string;
}

export default function Faq() {
  const { t } = useTranslation();
  let [isOpen, setOpen] = useState<boolean>(false);
  let [activeTab, setActiveTab] = useState<number>(1);

  // Create FAQ data dynamically using translations
  const faq: FaqData[] = [
    {
      id: 1,
      title: t("faq.question1"),
      desc: t("faq.answer1"),
    },
    {
      id: 2,
      title: t("faq.question2"),
      desc: t("faq.answer2"),
    },
    {
      id: 3,
      title: t("faq.question3"),
      desc: t("faq.answer3"),
    },
    {
      id: 4,
      title: t("faq.question4"),
      desc: t("faq.answer4"),
    },
    {
      id: 5,
      title: t("faq.question5"),
      desc: t("faq.answer5"),
    },
    {
      id: 6,
      title: t("faq.question6"),
      desc: t("faq.answer6"),
    },
  ];

  return (
    <div className="container relative">
      <div className="grid lg:grid-cols-2 md:grid-cols-1 grid-cols-1 items-center gap-8 lg:gap-12">
        {/* FAQ Content Section - Left side on desktop */}
        <div className="order-1 lg:order-1 lg:me-8 px-4 md:px-0">
          <h6 className="text-primaryColor uppercase text-sm font-bold tracking-wider mb-3">
            {t("faq.title")}
          </h6>
          <h4 className="mb-6 md:text-3xl text-2xl md:leading-normal leading-normal font-bold">
            {t("faq.subtitle")}
          </h4>
          <p className="text-slate-400 max-w-xl mx-auto pb-6">
            {t("faq.subtitle2")}
          </p>

          {/* FAQ Accordion */}
          <div id="accordion-collapseone" className="space-y-3 md:space-y-4">
            {faq.map((item: FaqData, index: number) => {
              return (
                <div
                  className={`relative bg-white dark:bg-gray-800 shadow-sm dark:shadow-gray-800 rounded-lg md:rounded-xl overflow-hidden border border-gray-100 dark:border-gray-700 transition-all duration-300 hover:shadow-md ${
                    activeTab === item.id
                      ? "ring-2 ring-primaryColor/20 shadow-md"
                      : ""
                  }`}
                  key={index}
                >
                  <h2 className="font-semibold">
                    <button
                      type="button"
                      onClick={() => setActiveTab(item.id)}
                      className={`flex justify-between items-center p-4 md:p-5 lg:p-6 w-full font-medium text-start transition-all duration-200 ${
                        activeTab === item.id
                          ? "bg-primaryColor/5 dark:bg-primaryColor/10 text-primaryColor"
                          : "text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700/50"
                      }`}
                    >
                      <span className="text-left pr-3 md:pr-4 text-sm md:text-base">
                        {item.title}
                      </span>
                      <FiChevronUp
                        className={`size-4 md:size-5 shrink-0 transition-transform duration-300 ${
                          activeTab === item.id ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  </h2>
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      activeTab === item.id
                        ? "max-h-96 opacity-100"
                        : "max-h-0 opacity-0"
                    }`}
                  >
                    <div className="p-4 md:p-5 lg:p-6 pt-0">
                      <p className="text-gray-600 dark:text-gray-400 text-sm md:text-base leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* CTA Section - Responsive */}
          <div className="mt-6 md:mt-8 p-4 md:p-5 lg:p-6 bg-gradient-to-r from-primaryColor/5 to-primaryColor/10 rounded-lg md:rounded-xl border border-primaryColor/20">
            <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-primaryColor rounded-lg md:rounded-xl flex items-center justify-center">
                  <FiDownload className="h-5 w-5 md:h-6 md:w-6 text-white" />
                </div>
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h4 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white mb-1">
                  {t("faq.ready")}
                </h4>
                <p className="text-gray-600 dark:text-gray-400 text-xs md:text-sm">
                  {t("faq.readyDescription")}
                </p>
              </div>
              <Link1
                className="w-full sm:w-auto px-4 py-2 md:py-3 bg-primaryColor text-white rounded-lg hover:bg-primaryColor/90 transition-colors duration-200 text-sm font-medium nav-link text-center"
                activeClass="active"
                spy={true}
                smooth={true}
                duration={200}
                to="home"
              >
                {t("faq.download")}
              </Link1>
            </div>
          </div>
        </div>

        {/* Phone Section - Right side on desktop */}
        <div className="relative order-2 lg:order-2 hidden md:block">
          <div className="relative">
            <img
              src={faqImg}
              className="mx-auto w-full max-w-sm lg:max-w-md drop-shadow-2xl"
              alt="Bossie App Preview"
            />

            <div className="absolute top-1/4 md:top-1/3 lg:top-24 right-4 md:right-8 lg:right-14 text-center">
              <button
                onClick={() => setOpen(true)}
                className="group size-16 md:size-18 lg:size-20 rounded-full shadow-lg bg-white dark:bg-gray-800 border-4 border-white dark:border-gray-700 hover:scale-110 transition-all duration-300 flex items-center justify-center"
              >
                <div className="size-10 md:size-11 lg:size-12 bg-primaryColor rounded-full flex items-center justify-center group-hover:bg-primaryColor/90 transition-colors duration-200">
                  <FiPlay className="h-4 w-4 md:h-5 md:w-5 text-white ml-0.5" />
                </div>
              </button>
            </div>
            <ModalVideo
              channel="youtube"
              youtube={{ mute: 0, autoplay: 0 }}
              isOpen={isOpen}
              videoId="S_CGed6E610"
              onClose={() => setOpen(false)}
            />
          </div>

          {/* Background Gradient - Responsive sizing */}
          <div className="overflow-hidden absolute md:size-[400px] lg:size-[500px] size-[300px] bg-gradient-to-tr to-primaryColor/20 via-primaryColor/70 from-primaryColor bottom-1/2 translate-y-1/2 right-0 md:right-0 lg:right-0 -z-1 shadow-lg shadow-primaryColor/20 rounded-full blur-sm"></div>

          {/* Stats Card - Responsive positioning and sizing */}
          <div className="absolute -bottom-6 md:-bottom-8 lg:-bottom-8 -left-4 md:-left-5 lg:-left-8 p-4 md:p-5 lg:p-6 rounded-xl md:rounded-2xl shadow-xl dark:shadow-gray-800 bg-white dark:bg-gray-800 w-56 md:w-60 lg:w-64 m-2 md:m-3 border border-gray-100 dark:border-gray-700">
            <div className="flex items-center">
              <div className="flex items-center justify-center size-[50px] md:size-[60px] lg:size-[65px] bg-primaryColor/10 text-primaryColor text-center rounded-xl md:rounded-2xl me-3">
                <FiDownload className="size-5 md:size-6" />
              </div>
              <div className="flex-1">
                <h6 className="text-gray-500 dark:text-gray-400 text-xs md:text-sm font-medium">
                  {t("faq.totalUsers")}
                </h6>
                <p className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">
                  <CountUp
                    className="counter-value"
                    end={45485}
                    duration={2.5}
                    separator=","
                  />
                </p>
                <div className="flex items-center mt-1">
                  <div className="flex text-yellow-400">
                    <span className="text-xs">★★★★★</span>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                    4.9/5
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
